import {
  IContactRespSchema,
  ICreditAndRebillReqSchema,
  IInvoiceRespSchema,
  InvoiceContactEditBehaviorEnum,
  ITaxConnectionBaseSchema,
  QuoteSettingsDefaultAddressSourceEnum,
  TaxStatusEnum,
} from '@monetize/types/app';
import { isPast } from 'date-fns/isPast';
import { parseISO } from 'date-fns/parseISO';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { doGetContactById } from '../../api/contactsService';
import { useGetListData } from '../../api/queryUtils';
import {
  useGetEarliestManualPaymentDate,
  useGetInvoiceSettings,
} from '../../api/settingsService';
import { AccountAddressSelect } from '../../components/Account/AccountAddressSelect';
import { ContactSelect } from '../../components/Contacts/ContactSelect';
import {
  MButton,
  MDivider,
  MFormField,
  MGrid,
  MLockedTextOrContent,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
} from '../../components/Monetize';
import { useCustomSelectValue } from '../../components/Monetize/MCustomSelect/useCustomSelectValue';
import { MTooltip } from '../../components/Monetize';
import { getAddressFromContact } from '../../utils/address';

interface EditUnpaidInvoiceContactFormProps {
  control: Control<ICreditAndRebillReqSchema>;
  setValue: UseFormSetValue<ICreditAndRebillReqSchema>;
  invoice: IInvoiceRespSchema;
  handleEdit: (contactId?: string) => void;
  canUpdateContact: boolean;
  canCreateContact: boolean;
  canUpdateBilling: boolean;
  hasCrmConfigured: boolean;
  defaultBillingAddressId?: string;
  defaultShippingAddressId?: string;
}

export const EditUnpaidInvoiceContactForm = ({
  control,
  setValue,
  invoice,
  handleEdit,
  canUpdateContact,
  canCreateContact,
  canUpdateBilling,
  hasCrmConfigured,
  defaultBillingAddressId,
  defaultShippingAddressId,
}: EditUnpaidInvoiceContactFormProps) => {
  const { data: isTaxProviderConnected } = useGetListData<
    ITaxConnectionBaseSchema,
    boolean
  >('tax', undefined, {
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
    select: (data) => {
      const taxProvider = data as unknown as ITaxConnectionBaseSchema[];
      return taxProvider?.some(({ status }) => status === TaxStatusEnum.ACTIVE);
    },
  });

  const { data: invoiceSettings } = useGetInvoiceSettings();
  const behavior =
    invoiceSettings?.invoiceCustomizationSettings?.invoiceContactEditBehavior;

  const { data: earliestPaymentDateSetttings } =
    useGetEarliestManualPaymentDate();

  const isPeriodOpen =
    !!earliestPaymentDateSetttings?.earliestManualPaymentDate &&
    isPast(parseISO(earliestPaymentDateSetttings.earliestManualPaymentDate));

  const isEnabled =
    behavior === InvoiceContactEditBehaviorEnum.ENABLED ||
    (behavior === InvoiceContactEditBehaviorEnum.ENABLED_IF_PERIOD_OPEN &&
      isPeriodOpen);

  const addressSource = useWatch({
    control,
    name: 'addressInformation.addressSource',
  });
  const billingContactId = useWatch({ control, name: 'billingContactId' });
  const shippingContactId = useWatch({ control, name: 'shippingContactId' });

  const isReadOnly = !canUpdateBilling;
  const isReadOnlyProps = {
    isDisabled: isReadOnly,
    isReadOnly,
    variant: 'readonly',
  } as const;
  const isAddressSourceContact =
    addressSource === QuoteSettingsDefaultAddressSourceEnum.CONTACT;

  const billingContactCustomValue = useCustomSelectValue<IContactRespSchema>({
    value: billingContactId,
    setValue: (val) => {
      setValue('billingContactId', val, { shouldDirty: true });
    },
    getOneById: doGetContactById,
  });

  const billingAddressTextForAddressSourceContact =
    billingContactCustomValue.internalValue
      ? getAddressFromContact(billingContactCustomValue.internalValue, {
          addressFormat: invoice.fromCompany.addressFormat,
        }).fullAddress
      : 'Address populates from Billing Contact';

  const shippingContactCustomValue = useCustomSelectValue<IContactRespSchema>({
    value: shippingContactId,
    setValue: (val) => {
      setValue('shippingContactId', val, { shouldDirty: true });
    },
    getOneById: doGetContactById,
  });

  const shippingAddressTextForAddressSourceContact =
    shippingContactCustomValue.internalValue
      ? getAddressFromContact(shippingContactCustomValue.internalValue, {
          addressFormat: invoice.fromCompany.addressFormat,
        }).fullAddress
      : 'Address populates from Billing Contact';

  if (!isEnabled) {
    return null;
  }

  const accountId = invoice.account.id;

  return (
    <>
      <MFormField
        label="Addresses Sourced From"
        isHorizontal
        mb={4}
        labelProps={{ mb: 0 }}
        tooltip="Set source of Shipping and Billing Addresses on this Invoice"
      >
        <Controller
          control={control}
          name="addressInformation.addressSource"
          render={({ field: { value, onChange, ...rest } }) => (
            <MTooltip
              label="Cannot change address source when tax provider is configured"
              isDisabled={!isTaxProviderConnected}
              shouldWrapChildren
            >
              <MRadioGroup
                onChange={onChange}
                value={value}
                {...rest}
                isDisabled={isReadOnly}
              >
                <MStack gap={4} direction="row" color="tGray.darkPurple">
                  <MRadio
                    value={QuoteSettingsDefaultAddressSourceEnum.ACCOUNT}
                    isDisabled={!hasCrmConfigured || isTaxProviderConnected}
                    onChange={() => {
                      setValue(
                        'addressInformation.billingAddressId',
                        defaultBillingAddressId,
                      );
                      setValue(
                        'addressInformation.shippingAddressId',
                        defaultShippingAddressId,
                      );
                    }}
                  >
                    <MTooltip
                      label="CRM must be connected to use Account Addresses"
                      placement="bottom-start"
                      shouldWrapChildren
                      isDisabled={hasCrmConfigured}
                    >
                      <MText
                        display="inline-flex"
                        alignItems="center"
                        color="currentColor"
                      >
                        Account Addresses
                      </MText>
                    </MTooltip>
                  </MRadio>
                  <MRadio
                    value={QuoteSettingsDefaultAddressSourceEnum.CONTACT}
                    isDisabled={isTaxProviderConnected}
                    onChange={() => {
                      setValue(
                        'addressInformation.billingAddressId',
                        undefined,
                      );
                      setValue(
                        'addressInformation.shippingAddressId',
                        undefined,
                      );
                    }}
                  >
                    Contacts
                  </MRadio>
                </MStack>
              </MRadioGroup>
            </MTooltip>
          )}
        />
      </MFormField>

      {/* Column flow keeps the single-column order (contact, address,
          contact, address) while pairing contacts on row 1 and addresses on
          row 2 once there is room for two columns. */}
      <MGrid
        templateColumns={{ base: '1fr', xl: 'repeat(2, 1fr)' }}
        templateRows={{ xl: 'repeat(2, auto)' }}
        autoFlow={{ base: 'row', xl: 'column' }}
        alignItems="start"
        gap={4}
      >
        <MFormField
          isRequired
          label="Billing Contact"
          labelRightComponent={
            canUpdateContact &&
            billingContactCustomValue.internalValue && (
              <MButton
                size="xs"
                mb="2"
                onClick={() => {
                  handleEdit(billingContactCustomValue.internalValue?.id);
                }}
                {...isReadOnlyProps}
                variant="tertiary"
              >
                Edit
              </MButton>
            )
          }
        >
          <ContactSelect
            showAddContacts={canCreateContact}
            accountId={accountId}
            loading={billingContactCustomValue.isLoading}
            value={billingContactCustomValue.internalValue}
            returnItem
            onChange={(val) => {
              billingContactCustomValue.onInternalValueChange(
                val as IContactRespSchema,
              );
            }}
            clearable={false}
            {...isReadOnlyProps}
          />
        </MFormField>

        {!isTaxProviderConnected && (
          <>
            <MFormField label="Billing Address">
              <MLockedTextOrContent
                isLocked={isAddressSourceContact}
                text={billingAddressTextForAddressSourceContact}
              >
                <Controller
                  name="addressInformation.billingAddressId"
                  control={control}
                  render={({ field }) => (
                    <AccountAddressSelect
                      {...field}
                      addressFormat={invoice.fromCompany.addressFormat}
                      accountId={accountId}
                      {...isReadOnlyProps}
                    />
                  )}
                />
              </MLockedTextOrContent>
            </MFormField>
            <MFormField
              isRequired
              label="Shipping Contact"
              labelRightComponent={
                canUpdateContact &&
                shippingContactCustomValue.internalValue && (
                  <MButton
                    size="xs"
                    mb="2"
                    onClick={() => {
                      handleEdit(shippingContactCustomValue.internalValue?.id);
                    }}
                    {...isReadOnlyProps}
                    variant="tertiary"
                  >
                    Edit
                  </MButton>
                )
              }
            >
              <ContactSelect
                showAddContacts={canCreateContact}
                accountId={accountId}
                loading={shippingContactCustomValue.isLoading}
                value={shippingContactCustomValue.internalValue}
                returnItem
                onChange={(val) => {
                  shippingContactCustomValue.onInternalValueChange(
                    val as IContactRespSchema,
                  );
                }}
                clearable={false}
                {...isReadOnlyProps}
              />
            </MFormField>

            <MFormField label="Shipping Address">
              <MLockedTextOrContent
                isLocked={isAddressSourceContact}
                text={shippingAddressTextForAddressSourceContact}
              >
                <Controller
                  name="addressInformation.shippingAddressId"
                  control={control}
                  render={({ field }) => (
                    <AccountAddressSelect
                      {...field}
                      addressFormat={invoice.fromCompany.addressFormat}
                      accountId={accountId}
                      {...isReadOnlyProps}
                    />
                  )}
                />
              </MLockedTextOrContent>
            </MFormField>
          </>
        )}
      </MGrid>

      <MDivider my={4} />
    </>
  );
};
